import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isRTL } from '@/core/i18n'
import {
  Plus,
  Search,
  Printer,
  Edit,
  Check,
  Mail,
  Copy,
  FileDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { showPostSuccess, showPostError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { ProcessingModal, ProcessingStepStatus } from '@/components/shared/ProcessingModal'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/store/auth.store'
import { invoicesService } from '@/services/invoicesService'
import { financeService } from '@/services/financeService'
import { InvoiceListItem, FinanceCustomer, ApiResponse } from '@/api/types'

export const InvoicesList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const rtl = isRTL(i18n.language)

  const [invoices, setInvoices] = useState<InvoiceListItem[]>([])
  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined)
  const [postingInvoiceId, setPostingInvoiceId] = useState<number | null>(null)
  const [showProcessingModal, setShowProcessingModal] = useState(false)
  const [processingSteps, setProcessingSteps] = useState<
    Array<{ label: string; status: ProcessingStepStatus }>
  >([
    { label: t('invoices.preparingXml'), status: 'pending' },
    { label: t('invoices.sendingToZatca'), status: 'pending' },
    { label: t('invoices.receivingQr'), status: 'pending' },
    { label: t('invoices.updatingQr'), status: 'pending' },
  ])

  const branchId = user?.selectedBranch?.branchId

  // Load customers for filter
  useEffect(() => {
    if (!branchId) return
    const loadCustomers = async () => {
      try {
        const customerList = await financeService.getAllCustomers(branchId)
        setCustomers(customerList)
      } catch (error: any) {
        // Error handled silently
      }
    }
    loadCustomers()
  }, [branchId])

  // Load invoices
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadInvoices = async () => {
      try {
        setLoading(true)
        const response = await invoicesService.getInvoices(branchId, pageNumber, pageSize)
        const list = response.data || []
        setInvoices(list)
        setTotalCount(response.totalRecords || list.length || 0)
        const pages = response.totalRecords ? Math.ceil(response.totalRecords / response.pageSize) : 0
        setTotalPages(pages || 1)
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to load invoices')
      } finally {
        setLoading(false)
      }
    }

    loadInvoices()
  }, [branchId, pageNumber, pageSize])

  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      if (selectedCustomerId && invoice.customerId !== selectedCustomerId) {
        return false
      }

      if (!searchQuery.trim()) return true
      const query = searchQuery.toLowerCase()
      return (
        (invoice.invoiceNumber || invoice.invoiceNo || '').toLowerCase().includes(query) ||
        (invoice.customerName || '').toLowerCase().includes(query) ||
        (invoice.projectName || '').toLowerCase().includes(query) ||
        (invoice.refNumber || invoice.refNo || invoice.poNumber || '').toLowerCase().includes(query)
      )
    })
  }, [invoices, selectedCustomerId, searchQuery])

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const handleAddInvoice = () => {
    navigate('/invoices/new')
  }

  const handleEditInvoice = (invoice: InvoiceListItem) => {
    const invoiceId = invoice.invoiceId ?? invoice.id
    if (!invoiceId) {
      toast.error(t('invoices.invalidInvoiceId'))
      return
    }
    navigate(`/invoices/${invoiceId}/edit`, { state: { invoice } })
  }

  const handlePostInvoice = async (invoice: InvoiceListItem) => {
    const invoiceId = invoice.invoiceId ?? invoice.id
    if (!invoiceId) {
      toast.error(t('invoices.invalidInvoiceId'))
      return
    }
    const isPosted = invoice.isPosted === true || invoice.isPosted === 1 || invoice.status?.toLowerCase() === 'posted'
    if (isPosted) {
      toast.error(t('invoices.invoiceAlreadyPosted'))
      return
    }

    // Reset steps and show modal
    setProcessingSteps([
      { label: t('invoices.preparingXml'), status: 'pending' },
      { label: t('invoices.sendingToZatca'), status: 'pending' },
      { label: t('invoices.receivingQr'), status: 'pending' },
      { label: t('invoices.updatingQr'), status: 'pending' },
    ])
    setShowProcessingModal(true)
    setPostingInvoiceId(invoiceId)

    try {
      // Step 1: Preparing XML
      await new Promise((resolve) => setTimeout(resolve, 800))
      setProcessingSteps((prev) =>
        prev.map((step, idx) => (idx === 0 ? { ...step, status: 'completed' as ProcessingStepStatus } : step))
      )

      // Step 2: Sending to Zatca
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setProcessingSteps((prev) =>
        prev.map((step, idx) => (idx === 1 ? { ...step, status: 'completed' as ProcessingStepStatus } : step))
      )

      // Step 3: Receiving QR
      await new Promise((resolve) => setTimeout(resolve, 800))
      setProcessingSteps((prev) =>
        prev.map((step, idx) => (idx === 2 ? { ...step, status: 'completed' as ProcessingStepStatus } : step))
      )

      // Make API call
      const response = await invoicesService.postInvoice(invoiceId)

      // Step 4: Updating QR
      await new Promise((resolve) => setTimeout(resolve, 500))
      setProcessingSteps((prev) =>
        prev.map((step, idx) => (idx === 3 ? { ...step, status: 'completed' as ProcessingStepStatus } : step))
      )

      // Wait a bit before closing modal
      await new Promise((resolve) => setTimeout(resolve, 500))

      const message =
        (response as ApiResponse)?.message ||
        (response as ApiResponse)?.data?.message ||
        (response as any)?.msg ||
        t('invoices.invoicePostedSuccess')
      
      setShowProcessingModal(false)
      await showPostSuccess(message)

      // Refresh list
      if (branchId) {
        const updated = await invoicesService.getInvoices(branchId, pageNumber, pageSize)
        setInvoices(updated.data || [])
        setTotalCount(updated.totalRecords || 0)
        const pages = updated.totalRecords ? Math.ceil(updated.totalRecords / updated.pageSize) : 0
        setTotalPages(pages || 1)
      }

      setShowProcessingModal(false)
    } catch (error: any) {
      // Error handled silently
      
      // Mark current step as error
      setProcessingSteps((prev) => {
        const currentStepIndex = prev.findIndex((step) => step.status === 'pending')
        if (currentStepIndex !== -1) {
          return prev.map((step, idx) =>
            idx === currentStepIndex ? { ...step, status: 'error' as ProcessingStepStatus } : step
          )
        }
        return prev
      })

      // Wait a bit before closing modal and showing error
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setShowProcessingModal(false)
      await showPostError(error)
    } finally {
      setPostingInvoiceId(null)
    }
  }

  const handleEInvoice = (invoiceId?: number) => {
    if (!invoiceId) {
      toast.error(t('invoices.invalidInvoiceId'))
      return
    }

    navigate(`/invoices/${invoiceId}/print`)
  }

  const handleEmailInvoice = (invoiceId?: number) => {
    if (!invoiceId) return
    toast.success(t('invoices.emailFunctionalityComingSoon'))
  }

  const handleCopy = () => {
    toast.success(t('invoices.copyFunctionalityComingSoon'))
  }

  const handleCsv = () => {
    toast.success(t('invoices.csvExportComingSoon'))
  }

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  const handleCustomerFilterChange = (value: string) => {
    if (value === 'all') {
      setSelectedCustomerId(undefined)
    } else {
      setSelectedCustomerId(Number(value))
    }
    setPageNumber(1)
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={t('navigation.invoices')}
          description={t('invoices.manageInvoices')}
        />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('invoices.branchNotSelected')}
              description={t('invoices.selectBranchToViewInvoices')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Processing Modal */}
      <ProcessingModal open={showProcessingModal} steps={processingSteps} />

      <PageHeader
        title={t('navigation.invoices')}
        description={t('invoices.manageInvoices')}
        action={{
          label: t('invoices.addNew'),
          onClick: handleAddInvoice,
          icon: Plus,
        }}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <label className="text-sm font-medium mb-2 block">{t('invoices.customer')}</label>
              <Select value={selectedCustomerId ? String(selectedCustomerId) : 'all'} onValueChange={handleCustomerFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t('invoices.all')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('invoices.all')}</SelectItem>
                  {customers.map(customer => (
                    <SelectItem key={customer.customerId} value={String(customer.customerId)}>
                      {customer.customerNameEn || customer.customerNameAr || `Customer ${customer.customerId}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 w-full">
              <label className="text-sm font-medium mb-2 block">{t('invoices.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('invoices.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="outline" size="sm" onClick={handleCopy} className="flex-1 sm:flex-initial">
                <Copy className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">{t('invoices.copy')}</span>
              </Button>
              <Button variant="outline" size="sm" onClick={handleCsv} className="flex-1 sm:flex-initial">
                <FileDown className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">{t('invoices.csv')}</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">{t('invoices.loadingInvoices')}</p>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={searchQuery || selectedCustomerId ? t('invoices.noInvoicesFound') : t('invoices.noInvoicesYet')}
                description={
                  searchQuery || selectedCustomerId
                    ? t('invoices.tryAdjustingSearch')
                    : t('invoices.getStarted')
                }
                action={
                  !searchQuery && !selectedCustomerId
                    ? {
                        label: t('invoices.addNewInvoiceAction'),
                        onClick: handleAddInvoice,
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 bg-primary/5">
                      <TableHead className={`font-bold text-primary px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('invoices.invoiceNumber')}</TableHead>
                      <TableHead className={`font-bold text-primary px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('invoices.customer')}</TableHead>
                      <TableHead className={`font-bold text-primary hidden lg:table-cell px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('invoices.project')}</TableHead>
                      <TableHead className={`font-bold text-primary hidden md:table-cell px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('invoices.reference')}</TableHead>
                      <TableHead className={`font-bold text-primary px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('invoices.date')}</TableHead>
                      <TableHead className="font-bold text-primary text-right px-2 sm:px-4">{t('invoices.amount')}</TableHead>
                      <TableHead className={`font-bold text-primary px-2 sm:px-4 ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>{t('invoices.status')}</TableHead>
                      <TableHead className="font-bold text-primary text-center px-2 sm:px-4">{t('invoices.eInv')}</TableHead>
                      <TableHead className="font-bold text-primary text-center px-2 sm:px-4">{t('common.edit')}</TableHead>
                      <TableHead className="font-bold text-primary text-center px-2 sm:px-4">{t('invoices.post')}</TableHead>
                      <TableHead className="font-bold text-primary text-center px-2 sm:px-4">{t('invoices.email')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.map((invoice) => {
                      const invoiceId = invoice.invoiceId ?? invoice.id
                      const isPosted = invoice.invStatus === 'Posted' || invoice.status?.toLowerCase() === 'posted'
                      return (
                        <TableRow key={invoiceId} className="border-b hover:bg-primary/5 transition-colors">
                          <TableCell className={`font-medium px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>{invoice.invoiceNumber || invoice.invoiceNo || `INV-${invoiceId}`}</TableCell>
                          <TableCell className={`px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>{invoice.customerName || 'N/A'}</TableCell>
                          <TableCell className={`hidden lg:table-cell px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>{invoice.projectName || (invoice.projectId ? `Project ${invoice.projectId}` : '-')}</TableCell>
                          <TableCell className={`hidden md:table-cell px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>{invoice.refNumber || invoice.refNo || invoice.poNumber || '-'}</TableCell>
                          <TableCell className={`px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>{formatDate(invoice.invoiceDate)}</TableCell>
                          <TableCell className="text-right px-2 sm:px-4">{formatAmount(invoice.total ?? invoice.amount)}</TableCell>
                          <TableCell className={`px-2 sm:px-4 ${rtl ? 'text-right' : 'text-left'}`}>
                            <Badge variant={isPosted ? 'default' : 'secondary'}>
                              {isPosted ? t('invoices.posted') : t('invoices.notPosted')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center px-2 sm:px-4">
                            <Button variant="ghost" size="icon" onClick={() => handleEInvoice(invoiceId)}>
                              <Printer className="h-4 w-4" />
                            </Button>
                          </TableCell>
                          <TableCell className="text-center px-2 sm:px-4">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditInvoice(invoice)}
                              title={isPosted ? t('invoices.invoiceAlreadyPosted') : t('invoices.editInvoice')}
                              disabled={isPosted}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TableCell>
                          <TableCell className="text-center px-2 sm:px-4">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePostInvoice(invoice)}
                              disabled={isPosted || postingInvoiceId === invoiceId}
                              className={isPosted ? 'text-green-600' : ''}
                            >
                              {postingInvoiceId === invoiceId ? (
                                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></span>
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                            </Button>
                          </TableCell>
                          <TableCell className="text-center px-2 sm:px-4">
                            <Button variant="ghost" size="icon" onClick={() => handleEmailInvoice(invoiceId)}>
                              <Mail className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  {t('invoices.showing')} {(pageNumber - 1) * pageSize + 1} {t('invoices.to')}{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} {t('invoices.of')} {totalCount} {t('invoices.entries')}
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">{t('invoices.pageSize')}:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('invoices.previous')}</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap">
                    {t('invoices.page')} {pageNumber} {t('invoices.of')} {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => (prev < totalPages ? prev + 1 : prev))}
                    disabled={pageNumber >= totalPages}
                  >
                    <span className="hidden sm:inline">{t('invoices.next')}</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
